import { Middleware } from '@reduxjs/toolkit'
import { isAuth0, isInternalWebsite } from '../../utils/helpers.ts'
import { newRelic } from '../../utils/new-relic.ts'

function isErrorAction(
  action: unknown
): action is { payload: { status: string | number } } {
  if (typeof action === 'object' && action !== null && 'payload' in action) {
    const payload = (action as Record<string, unknown>).payload
    return (
      typeof payload === 'object' && payload !== null && 'status' in payload
    )
  }
  return false
}

export const authMiddleware: Middleware = () => (next) => (action: unknown) => {
  if (isErrorAction(action) && action.payload?.status === 'FETCH_ERROR') {
    // This is what kicks off a silent refresh, so it's the trigger point for
    // the refresh-fails-then-redirects sequence. Recorded here to give the
    // replay a marker for where the sequence started.
    newRelic.noticeError(new Error('request failed to reach the server'), {
      context: 'authMiddlewareFetchError',
    })

    if (isAuth0 && !isInternalWebsite) {
      window.dispatchEvent(new Event('auth0-silent-refresh'))
      return next(action)
    }

    const ideyaApiUrl = location.protocol + '//' + location.host + '/'

    fetch(`${ideyaApiUrl}cdn-cgi/access/get-identity`, {
      credentials: 'include',
    }).then((res) => {
      if (res.status !== 200) {
        newRelic.noticeError(new Error('cloudflare identity probe failed'), {
          context: 'authMiddlewareReload',
        })
        window.location.reload()
      }
    })
  }

  if (
    isErrorAction(action) &&
    (action.payload!.status === 401 || action.payload!.status === 403)
  ) {
    const meta = (action as Record<string, unknown>).meta
    const baseQueryMeta =
      typeof meta === 'object' && meta !== null
        ? (meta as Record<string, unknown>).baseQueryMeta
        : undefined
    const request =
      typeof baseQueryMeta === 'object' && baseQueryMeta !== null
        ? (baseQueryMeta as Record<string, unknown>).request
        : undefined
    const requestUrl = request instanceof Request ? request.url : undefined

    if (!requestUrl?.includes('/verify')) {
      // Record before navigating: location.href ends the session replay, so a
      // notice sent after this line never reaches New Relic. This is the
      // "logged out for no reason" path users report.
      newRelic.noticeError(
        new Error(`forced logout on ${action.payload!.status}`),
        { context: 'authMiddlewareForcedLogout', requestUrl: requestUrl ?? '' }
      )
      window.location.href = '/logout'
    }
  }

  return next(action)
}
